import React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";

/**
 * RedMed design tokens.
 * Custom tokens live under palette.rm so they can be used in sx, e.g. sx={{ borderColor: "rm.border" }}.
 */
const tokens = {
  primary: "#5B21B6",
  primaryTint: "#EEE8FB",
  primaryOnTint: "#3B1A84",
  primaryDeep: "#2E1065",
  disabledBg: "#D9CCF2",
  disabledText: "#5E5878",
  page: "#F4F3F8",
  tile: "#FAF9FD",
  bonusBg: "#FFF1E6",
  bonusText: "#9A3412",
  sabbaqliBg: "#FFF4EB",
  sabbaqliBorder: "#F3C9A6",
  // Idoom cards
  idoom: "#1E4E9B",
  idoomChip: "#EEF3FB",
  unavailableText: "#6B6585",
  adslBands: { 500: "#2F7D1E", 1000: "#B0105F", 2000: "#0B6FA8" },
  // Payments
  mutedText: "#A09BB5",
  selectedRow: "#F7F4FE",
  // Operation result
  okMsgBg: "#F0FDF4",
  okMsgBorder: "#BBF7D0",
  okMsgText: "#14532D",
  failMsgBorder: "#FECACA",
  failMsgText: "#7F1D1D",
  shadowCard: "0 1px 2px rgba(23, 20, 43, .04)",
  // Account (Particulier)
  summaryAccent: "#FDBA74",
  progressTrack: "#F0EEF5",
  // Transactions
  chipBg: "#F2F0F7",
  failBg: "#FEF2F2",
  segmentShadow: "0 1px 2px rgba(23, 20, 43, .08)",
  // Home
  shadowLight: "0 1px 2px rgba(23, 20, 43, .04)",
  shadowFab: "0 8px 20px rgba(59, 26, 132, .28)",
  infoBg: "#E9F4FE",
  infoBorder: "#C3DFF8",
  infoText: "#0B3A66",
  dangerBorder: "#FCA5A5",
  warningBorder: "#FDBA74",
  mediaBg: "#EFEDF5",
  dot: "#CFC9E0",
  keycapBorder: "#CFC9E0",
  banner: {
    mobilis: { bg: "#1F6B2A", pill: "#175321", text: "#FFFFFF" },
    accessories: { bg: "#8E1F55", icon: "#741846", text: "#F6D8E7" },
    pubg: { bg: "#1C1A24", pill: "#33303F", accent: "#F5C451", text: "#CFCBD9" },
    freefire: { bg: "#9A3412", pill: "#7C2A0E", accent: "#FFFFFF", text: "#FFE4D2" },
  },
  surface: "#FFFFFF",
  border: "#E6E3EF",
  inputBorder: "#DCD8E8",
  rowDivider: "#F0EEF5",
  tableHead: "#FAF9FD",
  subtle: "#F7F6FB",
  track: "#E9E6F2",
  text: "#17142B",
  textSecondary: "#5E5878",
  textTertiary: "#3A3552",
  icon: "#4A4563",
  dark: "#17142B",
  darkRaised: "#24203A",
  darkBorder: "#3A3552",
  onDark: "#FFFFFF",
  onDarkMuted: "#CFC8E8",
  successBg: "#DCFCE7",
  successText: "#166534",
  dangerBg: "#FEE2E2",
  dangerText: "#991B1B",
  warningBg: "#FFEDD5",
  warningText: "#9A3412",
  logout: "#A0182A",
  online: "#16A34A",
  slow: "#D97706",
  offline: "#DC2626",
};

// Latin glyphs come from Plus Jakarta Sans; Arabic glyphs fall through to Noto Kufi Arabic.
const fontFamily = '"Plus Jakarta Sans", "Noto Kufi Arabic", "Segoe UI", system-ui, sans-serif';
export const arabicFontFamily = '"Noto Kufi Arabic", "Plus Jakarta Sans", sans-serif';

const radius = { card: 18, cardLg: 20, control: 12, controlLg: 14, tile: 14, controlSm: 10, pill: 999 };

const theme = createTheme({
  palette: {
    primary: { main: tokens.primary, light: tokens.primaryTint, dark: tokens.primaryOnTint, contrastText: "#FFFFFF" },
    success: { main: tokens.successText, light: tokens.successBg, contrastText: "#FFFFFF" },
    error: { main: tokens.dangerText, light: tokens.dangerBg, contrastText: "#FFFFFF" },
    warning: { main: tokens.warningText, light: tokens.warningBg, contrastText: "#FFFFFF" },
    background: { default: tokens.page, paper: tokens.surface },
    text: { primary: tokens.text, secondary: tokens.textSecondary },
    divider: tokens.border,
    rm: { ...tokens, radius },
  },
  shape: { borderRadius: radius.control },
  shadows: ["none", ...Array(24).fill("0 8px 24px rgba(23, 20, 43, 0.12)")],
  typography: {
    fontFamily,
    button: { textTransform: "none", fontWeight: 700 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { fontFamily } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // "&&" raises specificity above the global `.MuiButton-root { background-color: ... !important }` in style.css
        root: {
          "&&": {
            minHeight: 44,
            borderRadius: radius.control,
            padding: "0 18px",
            fontSize: 14,
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
            gap: 8,
          },
        },
        sizeSmall: { "&&": { minHeight: 38, borderRadius: radius.controlSm, padding: "0 12px", fontSize: 13 } },
        // The red legacy dialog buttons (.annuler_btn / .close_btn) keep their own colours.
        contained: {
          "&&:not(.annuler_btn):not(.close_btn)": { backgroundColor: `${tokens.primary} !important`, color: "#FFFFFF" },
          "&&:not(.annuler_btn):not(.close_btn):hover": { backgroundColor: `${tokens.primaryOnTint} !important` },
          // Disabled: clearly lighter, with a not-allowed cursor (the native disabled attribute still blocks clicks)
          "&&:not(.annuler_btn):not(.close_btn).Mui-disabled": {
            backgroundColor: `${tokens.disabledBg} !important`,
            color: tokens.disabledText,
            pointerEvents: "auto",
            cursor: "not-allowed",
          },
        },
        outlined: {
          "&&": {
            backgroundColor: `${tokens.surface} !important`,
            color: tokens.textTertiary,
            borderColor: tokens.inputBorder,
          },
          "&&:hover": { backgroundColor: `${tokens.subtle} !important`, borderColor: tokens.primary },
        },
        startIcon: { marginRight: 0 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.controlSm },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: radius.control,
          backgroundColor: tokens.surface,
          fontSize: 14,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.inputBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.textSecondary },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tokens.primary, borderWidth: 2 },
        },
        input: { padding: "11px 14px" },
      },
    },
    MuiSelect: {
      styleOverrides: { select: { padding: "11px 14px", minHeight: 0 } },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { fontSize: 13, fontWeight: 600, color: tokens.textTertiary, "&.Mui-focused": { color: tokens.textTertiary } },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: { backgroundColor: tokens.dark, fontSize: 12, fontWeight: 600, borderRadius: 8, padding: "6px 10px" },
        arrow: { color: tokens.dark },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tokens.rowDivider}`, fontSize: 14, color: tokens.text },
        head: {
          backgroundColor: tokens.tableHead,
          color: tokens.textSecondary,
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: ".06em",
          borderBottom: `1px solid ${tokens.border}`,
        },
      },
    },
  },
});

/** Shared style fragments, kept here so components don't re-declare them. */
export const tabularNums = { fontVariantNumeric: "tabular-nums" };

export const cardSx = {
  bgcolor: "rm.surface",
  border: "1px solid",
  borderColor: "rm.border",
  borderRadius: `${radius.card}px`,
  boxShadow: "none",
};

export const pillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  height: 28,
  px: "10px",
  borderRadius: `${radius.pill}px`,
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

/** Status pill colours keyed by tone. */
export const toneSx = {
  success: { bgcolor: "rm.successBg", color: "rm.successText" },
  danger: { bgcolor: "rm.dangerBg", color: "rm.dangerText" },
  warning: { bgcolor: "rm.warningBg", color: "rm.warningText" },
  neutral: { bgcolor: "rm.primaryTint", color: "rm.primaryOnTint" },
  bonus: { bgcolor: "rm.bonusBg", color: "rm.bonusText" },
  blocked: { bgcolor: "rm.dark", color: "#FFFFFF" },
};

/** 2px primary focus ring with 2px offset, for custom (ButtonBase / native) controls. */
export const focusRingSx = {
  "&:focus-visible, &.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "2px" },
};

export function RedMedThemeProvider({ children }) {
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default theme;
